import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import CloudIcon from "@mui/icons-material/Cloud";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import GrainIcon from "@mui/icons-material/Grain";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import AirIcon from "@mui/icons-material/Air";
import SpeedIcon from "@mui/icons-material/Speed";
import ExploreIcon from "@mui/icons-material/Explore";
import CompressIcon from "@mui/icons-material/Compress";
import Widget from "../Common/Widget/Widget";
import { SectionHeading } from "../Common/Typography/AppTypography";
import ParameterCard from "./ParameterCard";
import {
  formatLocalDateTime,
  titleCaseWeather,
} from "../../utils/smartRoomHelpers";
import { formatDisplayValue } from "../../utils/smartRoomStatusRanges";

const resolveWeatherIcon = (description = "") => {
  const d = description.toLowerCase();
  if (d.includes("thunder")) {
    return <ThunderstormIcon sx={{ fontSize: 64, color: "#5C6BC0" }} />;
  }
  if (d.includes("rain") || d.includes("drizzle")) {
    return <GrainIcon sx={{ fontSize: 64, color: "#0288D1" }} />;
  }
  if (d.includes("fog") || d.includes("mist") || d.includes("haze")) {
    return <BlurOnIcon sx={{ fontSize: 64, color: "#78909C" }} />;
  }
  if (d.includes("cloud")) {
    return <CloudIcon sx={{ fontSize: 64, color: "#90A4AE" }} />;
  }
  return <WbSunnyIcon sx={{ fontSize: 64, color: "#FBC02D" }} />;
};

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const OutdoorDashboard = ({ outdoor, lastUpdatedLabel }) => {
  const outdoorParams = [
    {
      name: "Temperature",
      value: outdoor?.temperature,
      unit: "°C",
      rangeKey: "temperature",
      icon: <ThermostatIcon fontSize="small" />,
    },
    {
      name: "Humidity",
      value: outdoor?.humidity,
      unit: "%",
      rangeKey: "humidity",
      icon: <WaterDropIcon fontSize="small" />,
    },
    {
      name: "PM2.5",
      value: outdoor?.pm25,
      unit: "µg/m³",
      rangeKey: "pm25",
      icon: <AirIcon fontSize="small" />,
    },
    {
      name: "Pressure",
      value: outdoor?.pressure,
      unit: "hPa",
      rangeKey: "pressure",
      icon: <CompressIcon fontSize="small" />,
    },
    {
      name: "Wind Speed",
      value: outdoor?.windSpeed,
      unit: "m/s",
      rangeKey: "windSpeed",
      icon: <SpeedIcon fontSize="small" />,
    },
    {
      name: "Wind Direction",
      value: outdoor?.windDirection,
      unit: outdoor?.windDegree != null ? `°${outdoor.windDegree}` : "",
      rangeKey: null,
      icon: <ExploreIcon fontSize="small" />,
      showProgress: false,
      statusOverride: "Observed",
      colorOverride: "#0288D1",
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <SectionHeading sx={{ mb: 0.5 }}>Outdoor</SectionHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Outdoor weather and environmental conditions near the selected space.
      </Typography>

      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 size={{ xs: 12, md: 5 }}>
          <Widget
            title="Weather Summary"
            subheader="Outdoor conditions near the selected space"
            disableHeaderDivider
            contentSx={{ pt: 0 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
              }}
            >
              {resolveWeatherIcon(outdoor?.description)}
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {titleCaseWeather(outdoor?.description)}
                </Typography>
                <Chip
                  size="small"
                  label={`${outdoor?.city || "—"}, ${outdoor?.country || "—"}`}
                  sx={{ mt: 1, fontWeight: 600 }}
                />
              </Box>
            </Box>

            <Grid2 container spacing={1.5}>
              <Grid2 size={4}>
                <DetailItem
                  label="Temperature"
                  value={`${formatDisplayValue(outdoor?.temperature)} °C`}
                />
              </Grid2>
              <Grid2 size={4}>
                <DetailItem
                  label="Humidity"
                  value={`${formatDisplayValue(outdoor?.humidity)} %`}
                />
              </Grid2>
              <Grid2 size={4}>
                <DetailItem
                  label="PM2.5"
                  value={`${formatDisplayValue(outdoor?.pm25)} µg/m³`}
                />
              </Grid2>
            </Grid2>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              Last Updated:{" "}
              {lastUpdatedLabel || formatLocalDateTime(outdoor?.timeStamp)}
            </Typography>
          </Widget>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 7 }}>
          <Widget
            title="Weather Details"
            subheader="Atmospheric information for the selected space"
            disableHeaderDivider
            contentSx={{ pt: 0 }}
          >
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 6, sm: 4 }}>
                <DetailItem
                  label="Pressure"
                  value={`${formatDisplayValue(outdoor?.pressure)} hPa`}
                />
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 4 }}>
                <DetailItem
                  label="Wind Speed"
                  value={`${formatDisplayValue(outdoor?.windSpeed, 2)} m/s`}
                />
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 4 }}>
                <DetailItem
                  label="Wind Direction"
                  value={`${outdoor?.windDirection || "—"} (${formatDisplayValue(
                    outdoor?.windDegree,
                    0
                  )}°)`}
                />
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 4 }}>
                <DetailItem
                  label="Sunrise"
                  value={formatLocalDateTime(outdoor?.sunrise)}
                />
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 4 }}>
                <DetailItem
                  label="Sunset"
                  value={formatLocalDateTime(outdoor?.sunset)}
                />
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 4 }}>
                <DetailItem
                  label="City / Country"
                  value={`${outdoor?.city || "—"} / ${outdoor?.country || "—"}`}
                />
              </Grid2>
            </Grid2>
          </Widget>
        </Grid2>
      </Grid2>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        Outdoor Conditions
      </Typography>
      <Grid2 container spacing={1.5}>
        {outdoorParams.map((param) => (
          <Grid2 key={param.name} size={{ xs: 12, sm: 6, md: 4 }}>
            <ParameterCard {...param} />
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
};

OutdoorDashboard.propTypes = {
  outdoor: PropTypes.object,
  lastUpdatedLabel: PropTypes.string,
};

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default OutdoorDashboard;
